import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import AddIcon from "@mui/icons-material/Add";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useModals, useNavigationHooks, useUrlHooks } from "hooks";
import { IApiServiceError, useClientActions, useClientIdSelector } from "store";

import EmptyCardList from "../../../assets/images/EmptyCardList.png";

import { AddEntityModal } from "./AddEntityModal";
import { EntitiesTable } from "./EntitiesTable";

export const EntitiesTab: React.FC = () => {
  const clientId = useClientIdSelector();
  const { getAllocations, deleteAllocation } = useClientActions();
  const { deleteModal } = useModals();
  const { queryParams } = useUrlHooks();
  const { removeParams } = useNavigationHooks();

  // -- Data via useActionHook (loading starts true for the initial load) --
  const loadingOpts = useMemo(() => ({ defaultLoadingState: true }), []);
  const {
    data: entities = [],
    loading: isLoading,
    error,
    action: getEntities
  } = useActionHook(getAllocations, loadingOpts);
  const hasError = Boolean(error);

  const fetchEntities = useCallback(() => {
    if (!clientId) return;
    getEntities(clientId).catch((err) => {
      console.error("[ClientEntities] Failed to load allocations:", err);
    });
  }, [clientId, getEntities]);

  useEffect(() => {
    fetchEntities();
  }, []);

  // -- Add Entity modal (opened from the header Add menu via `clientEntity=create`) --
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (queryParams?.clientEntity === "create") {
      setAddModalOpen(true);
      removeParams(["clientEntity"]);
    }
  }, [queryParams, queryParams?.clientEntity, removeParams]);

  // -- Remove entity via the shared delete modal --
  const handleRemoveClick = useCallback(
    (entityId: number) => {
      if (!clientId) return;
      const entity = entities.find((e) => e.id === entityId);
      deleteModal({
        element: "Remove Entity",
        onPressSuccess: "Remove",
        message: `Remove ${entity?.name ?? "this entity"} from this client? This will affect workspace file visibility and portfolio calculations.`,
        action: async () => {
          try {
            await deleteAllocation(clientId, entityId);
            toast.success("Entity removed");
            await fetchEntities();
          } catch (err: unknown) {
            const apiError = err as IApiServiceError;
            toast.error(apiError?.message ?? "Failed to remove entity.");
          }
        }
      });
    },
    [clientId, entities, deleteAllocation, deleteModal, fetchEntities]
  );

  const handleAddEntitySuccess = useCallback(() => {
    toast.success("Entity linked");
    fetchEntities();
  }, [fetchEntities]);

  // -- Render --
  return (
    <Stack sx={{ gap: "16px", width: "100%", height: "100%" }}>
      {/* White panel wrapper for content */}
      <Stack
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
          p: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flexGrow: 1,
          overflow: "hidden"
        }}
      >
        {/* S-1: Loading -- CircularProgress */}
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : entities.length === 0 && !hasError ? (
          /* S-3: Empty state */
          <EmptyCard
            title={EMPTY_CARD_CONTENT.clientEntities.title}
            description={{
              text: EMPTY_CARD_CONTENT.clientEntities.description,
              externalText: EMPTY_CARD_CONTENT.clientEntities.externalDescription
            }}
            image={{ src: EmptyCardList, sx: { width: 315, height: 125 } }}
            actionsElement={
              <Button
                variant="contained"
                onClick={() => setAddModalOpen(true)}
                startIcon={<AddIcon />}
                sx={{ bgcolor: "#071F35" }}
              >
                Add Entity
              </Button>
            }
          />
        ) : hasError ? (
          /* S-5: Error state */
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <Typography sx={{ fontSize: "14px" }}>Couldn&apos;t load entities.</Typography>
          </Stack>
        ) : (
          /* Normal state: render the table */
          <EntitiesTable entities={entities} onRemoveEntity={handleRemoveClick} />
        )}
      </Stack>

      {/* Add Entity modal */}
      {clientId !== undefined && (
        <AddEntityModal
          clientId={clientId}
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleAddEntitySuccess}
        />
      )}
    </Stack>
  );
};

export default EntitiesTab;
